import { matchGenesysLanguage } from './genesys-lang';
import { screenFollowsLanguage } from './screens';
import {
  DEFAULT_LANGUAGE,
  getDirection,
  normalizeStoredLanguage,
  type LanguageCode,
  type TextDirection,
} from './types';

export interface LanguageResolutionInput {
  /** Build switch. Off forces English and hides the link language. */
  languagesEnabled: boolean;
  /** False on install steps, Setup Complete and uninstall. */
  screenFollowsLanguage: boolean;
  /** Raw `mediabriz.wizard.lang` value. Only a manual pick is stored. */
  stored: string | null;
  /** Genesys `langTag`, when the launch URL sent one. */
  langTag: string | null;
}

export interface LanguageResolution {
  /** Language this screen shows. */
  language: LanguageCode;
  dir: TextDirection;
  /** Saved manual choice, including on a screen that ignores it. */
  manual: LanguageCode | null;
  /**
   * Language the Insights link should carry. Set only for a manual pick
   * while languages are switched on.
   */
  uiLang: LanguageCode | null;
}

/**
 * Saved choice, then the Genesys language, then English.
 * English-only screens and a switched-off build stay English and
 * left-to-right. The saved choice is kept either way.
 */
export function resolveWizardLanguage(input: LanguageResolutionInput): LanguageResolution {
  const manual = normalizeStoredLanguage(input.stored);
  const uiLang = input.languagesEnabled && manual ? manual : null;
  if (!input.languagesEnabled || !input.screenFollowsLanguage) {
    return { language: 'en', dir: 'ltr', manual, uiLang };
  }
  if (manual) {
    return { language: manual, dir: getDirection(manual), manual, uiLang };
  }
  const fromGenesys = matchGenesysLanguage(input.langTag) ?? DEFAULT_LANGUAGE;
  return { language: fromGenesys, dir: getDirection(fromGenesys), manual: null, uiLang: null };
}

export interface ScreenLanguageInput {
  page: string;
  languagesEnabled: boolean;
  stored: string | null;
  langTag: string | null;
  /**
   * Dev `?lang=` override. Ignored on English-only screens and when languages
   * are switched off. Not saved.
   */
  previewLang?: LanguageCode | null;
  /**
   * Uninstall confirm. English and left-to-right even while the page behind
   * the dialog is still the loading screen.
   */
  forceEnglish?: boolean;
}

/**
 * Language the screen actually shows. Install steps, Setup Complete and
 * uninstall ignore a saved choice, the Genesys tag, and a preview override.
 */
export function languageForScreen(input: ScreenLanguageInput): LanguageResolution {
  const follows = !input.forceEnglish && screenFollowsLanguage(input.page);
  const resolution = resolveWizardLanguage({
    languagesEnabled: input.languagesEnabled,
    screenFollowsLanguage: follows,
    stored: input.stored,
    langTag: input.langTag,
  });
  if (input.previewLang && input.languagesEnabled && follows) {
    return {
      ...resolution,
      language: input.previewLang,
      dir: getDirection(input.previewLang),
    };
  }
  return resolution;
}
